import { ChangeEvent, ReactNode, useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import * as R from 'ramda';

import { requestElementBatchCreate } from '@atom/actions/inventoryAssetActions';
import {
  requestInventorySchemaClear,
  retrieveInventorySchemaByAssetId,
} from '@atom/actions/inventorySchemaActions';
import NoElementsDialog from '@atom/components/common/elements/NoElementsDialog';
import { Button, Modal, Progress } from '@atom/mui';
import {
  filterSchemaByMarkerId,
  inventorySchema,
} from '@atom/selectors/schemaSelectors';
import { ElementGroupsItem } from '@atom/types/inventory';
import { RootState } from '@atom/types/store';
import { toggleFromSet } from '@atom/utilities/setUtilities';

import FhwaSchemaElementGroupSection from './FhwaSchemaElementGroupSection';
import FhwaSchemaElementListRow from './FhwaSchemaElementListRow';

import classes from './elements.module.css';

const styles = {
  dialog: {
    bodyStyle: { margin: 0, padding: 0 },
  },
  actionButtons: {
    cancel: { marginRight: '1rem' },
  },
};

type Props = {
  open?: boolean;
  closeModal?: () => void;
  title: string;
  assetId: string;
  assetName: string;
  onBatchCreate: (payload: Object) => void;
  existingSchemaIds: { [schemaId: string]: any };
  markerId?: any;
};

const FhwaAddAssetElementModal = (props: Props) => {
  const {
    open: initOpen,
    closeModal,
    title,
    assetId,
    assetName,
    onBatchCreate,
    existingSchemaIds,
  } = props;
  const dispatch = useDispatch();

  const [open, setOpen] = useState(false);
  const [selectedItems, setSelectedItems] = useState(new Set([]));
  const [schemaQuantities, setSchemaQuantities] = useState({});

  const loadingInventorySchema = useSelector(
    (state: RootState) => state.loading.loadingInventorySchema,
  );

  const loadingBatchCreation = useSelector(
    (state: RootState) => state.loading.loadingElementBatchCreation,
  );

  const loadingElementDelete = useSelector(
    (state: RootState) => state.loading.loadingElementDelete,
  );

  const loadingSchemaTreeNode = useSelector(
    (state: RootState) => state.loading.loadingSchemaTreeNode,
  );

  const schemaSlice = useSelector(inventorySchema);
  const schema: any = useMemo(
    () => filterSchemaByMarkerId(schemaSlice, { ...props, isFhwaForm: true }),
    [schemaSlice, props.markerId],
  );

  const setInitialState = () => {
    setOpen(false);
    setSelectedItems(new Set([]));
    setSchemaQuantities({});
  };

  const handleCloseModal = () => {
    setInitialState();

    if (!R.isEmpty(schema)) {
      dispatch(requestInventorySchemaClear());
    }

    closeModal();
  };

  useEffect(() => {
    if (initOpen) {
      dispatch(retrieveInventorySchemaByAssetId({ id: assetId }));
    }
    setOpen(initOpen);
  }, [initOpen]);

  const confirm = () => {
    const elements = [...selectedItems].reduce(
      (previous: any[], next: any): any => {
        const pathAndId = next.split('.');
        const schemaId = R.last(pathAndId);
        const path = R.dropLast(1, pathAndId);

        const batchItem = {
          schemaId,
          quantity: schemaQuantities[next] || 1,
        };

        if (R.isEmpty(path)) {
          return [...previous, batchItem];
        }

        const indexPath = path.reduce(
          (previousPath: any[], nextPathId: string): string[] => {
            const lens = R.lensPath(previousPath);
            const section = R.view(lens, previous);

            // @ts-ignore
            const nextIndex = section.findIndex(
              (item: any): boolean => item.schemaId === nextPathId,
            );
            return [...previousPath, nextIndex, 'elements'];
          },
          [],
        );

        // @ts-ignore
        const lens = R.lensPath(indexPath);

        const view = R.view(lens, previous) || [];
        // @ts-ignore
        return R.set(lens, [...view, batchItem], previous);
      },
      [],
    );

    const batchSchemaRequestBody = {
      assetId,
      assetSchemaId: schema.id,
      elements,
    };

    if (!R.isNil(onBatchCreate)) {
      onBatchCreate(batchSchemaRequestBody);
    } else {
      dispatch(requestElementBatchCreate(batchSchemaRequestBody));
    }

    handleCloseModal();
  };

  const setSchemaQuantity = (input: string): number => {
    const parsedValue = Math.floor(Number(input));
    if (parsedValue > 99) {
      return 99;
    }
    if (parsedValue < 1) {
      return 1;
    }

    return parsedValue;
  };

  const onChange = (
    _: ChangeEvent<HTMLInputElement>,
    name: string,
    value: any,
  ): void => {
    const schemaQuantity = setSchemaQuantity(value);
    setSchemaQuantities({ ...schemaQuantities, [name]: schemaQuantity });
  };

  const toggleChecked = (id: string) =>
    setSelectedItems(toggleFromSet(selectedItems, id));

  const renderElements = (): ReactNode => {
    if (R.isEmpty(schema) || R.isEmpty(schema.elementGroups)) {
      return <div />;
    }

    const elementGroups = schema.elementGroups;

    return elementGroups.map(
      (elementGroup: ElementGroupsItem, elementGroupIndex: number) => {
        if (elementGroup.isGroup) {
          return (
            <>
              <FhwaSchemaElementGroupSection
                key={elementGroup.name}
                elementGroupIndex={elementGroupIndex}
                loadingSchemaTreeNode={loadingSchemaTreeNode}
                onClick={toggleChecked}
                onChange={onChange}
                elementGroup={elementGroup}
                selectedItems={selectedItems}
                schemaQuantities={schemaQuantities}
              />
            </>
          );
        }

        const element = elementGroup.elements[0];
        const subElementExistsOnAsset = existingSchemaIds
          ? !!existingSchemaIds[element.id]
          : false;
        return (
          <div className={classes.elementListRowContainer} key={element.id}>
            <FhwaSchemaElementListRow
              isChecked={selectedItems.has(element.id)}
              onChange={onChange}
              loading={loadingSchemaTreeNode}
              // @ts-ignore
              element={element}
              onClick={toggleChecked}
              value={schemaQuantities[element.id]}
              selectedItems={selectedItems}
              schemaQuantities={schemaQuantities}
              elementPath={['elementGroups', elementGroupIndex, 'elements', 0]}
              elementExistsOnAsset={subElementExistsOnAsset}
            />
          </div>
        );
      },
    );
  };

  const addModalDisabledState = !selectedItems.size;

  const getFooter = () => {
    return (
      <>
        <Button sx={styles.actionButtons.cancel} onClick={handleCloseModal}>
          Cancel
        </Button>
        <Button
          disabled={addModalDisabledState}
          onClick={confirm}
          color="primary"
          variant="contained"
        >
          Add
        </Button>
      </>
    );
  };

  const dialogContent =
    loadingElementDelete ||
    loadingInventorySchema ||
    loadingBatchCreation ||
    R.isEmpty(schema) ? (
      <div className={classes.elementModalSpinnerContainer}>
        <Progress />
      </div>
    ) : (
      <div className={classes.elementModalContainer}>
        <div className={classes.elementModalSubheading}>
          Select elements and quantities from inventory type for
          <span className={classes.elementModalAssetName}>{assetName}</span>
        </div>
        <div className={classes.elementModalSelectionBackdrop}>
          {renderElements()}
        </div>
      </div>
    );

  const dialog = R.isEmpty(schema.elementGroups) ? (
    <NoElementsDialog
      title="No elements available."
      assetName={assetName}
      closeModal={handleCloseModal}
      open={open}
    />
  ) : (
    <Modal
      title={title}
      width="lg"
      footer={getFooter()}
      style={styles.dialog.bodyStyle}
      open={open}
      onCancel={handleCloseModal}
    >
      {dialogContent}
    </Modal>
  );

  return <div>{dialog}</div>;
};

export default FhwaAddAssetElementModal;
